'use client';

import { useRouter } from 'next/router';
import { type ReactNode, createContext } from 'react';

import { decodeQueryParam } from '../utils';
import type { ClaimFlowGetServerSidePropsPayload, Step } from './types';

export interface CardClaimContext {
  /**
   * ID of the card to be claimed
   * */
  cardId: string;
  /**
   * Name of the workspace admin who created the card to be claimed
   * */
  adminName: string;
  /**
   * Email of the workspace admin who created the card to be claimed
   * */
  adminEmail: string;
  /**
   * Name of the workspace to which the claimable card belongs
   * */
  companyName: string;
  /**
   * API Token that authorizes you to claim the profile that owns the claimable card
   * */
  claimProfileToken: string;
  /**
   * API Token that authorizes you to perform limited querying of our GraphQL graph without being logged in.
   * This token is scoped to a particular Card, and only authorizes queries / mutations on that card.
   *
   * Use this token as an Authorization header when making a GraphQL requests, like so:
   * ```
   * headers: {
   *   authorization: `ClaimCard ${claimCardToken}`,
   * }
   * ```
   * */
  claimCardToken: string;
  /**
   * Email of the profile that owns the unclaimed card
   * */
  profileEmail: string;
  /**
   * ID of the profile that owns the unclaimed card
   * */
  profileId: string;
  flowState: {
    currentStepIndex: number;
    currentStep: Step;
    nextStepUrl: string | null;
    previousStepUrl: string | null;
  };
}

export const cardClaimContext = createContext<CardClaimContext>({
  cardId: '',
  adminName: '',
  adminEmail: '',
  companyName: '',
  claimProfileToken: '',
  claimCardToken: '',
  profileEmail: '',
  profileId: '',
  flowState: {
    currentStep: 'get-started',
    currentStepIndex: 0,
    nextStepUrl: '',
    previousStepUrl: '',
  },
});

export const ClaimCardContext = ({
  claimFlowProps,
  children,
}: {
  claimFlowProps: ClaimFlowGetServerSidePropsPayload;
  experiments?: { [key: string]: boolean };
  children: ReactNode;
}): JSX.Element | null => {
  const router = useRouter();

  const { card: cardId, email, claim_token, profile_id } = router.query;

  const hashString = router.asPath.split('#')[1];
  if (!hashString) {
    return null;
  }

  const hashParams = Object.fromEntries(hashString.split('&').map((item) => item.split('=')));

  const { adminName, adminEmail, companyName, token: claimCardToken } = hashParams;

  const context: CardClaimContext = {
    cardId: cardId as string,
    adminEmail: decodeURIComponent(adminEmail || ''),
    adminName: decodeQueryParam(adminName || ''),
    companyName: decodeQueryParam(companyName + ''),
    claimProfileToken: claim_token as string,
    claimCardToken: claimCardToken as string,
    profileEmail: decodeURIComponent(email as string),
    profileId: profile_id as string,
    flowState: {
      currentStep: claimFlowProps.currentStep,
      currentStepIndex: claimFlowProps.currentStepIndex,
      nextStepUrl: claimFlowProps.nextStepUrl,
      previousStepUrl: claimFlowProps.previousStepUrl,
    },
  };

  return <cardClaimContext.Provider value={context}>{children}</cardClaimContext.Provider>;
};
